import { type IPublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { needsInteractiveSignIn } from './authErrors';
import { apiScope, loginRequest } from './authConfig';
import { isWakingResponse, shouldRetryWake, wakeRetryDelay } from './wakeRetry.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Guards against the login loop the comment below rightly warned about.
 *
 * If we redirect, come back, and immediately fail to get a token again, a
 * second redirect would spin. Redirecting at most once in this window means a
 * genuinely broken sign-in surfaces as an error the user can read and report,
 * rather than as a browser that never settles.
 */
const REDIRECT_GUARD_KEY = 'acutidy.lastAuthRedirect';
const REDIRECT_GUARD_MS = 20_000;

function redirectedVeryRecently(): boolean {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_GUARD_KEY) ?? '0');
    return Number.isFinite(last) && Date.now() - last < REDIRECT_GUARD_MS;
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). No guard is
    // better than no sign-in.
    return false;
  }
}

/**
 * Silent-first token acquisition. acquireTokenSilent covers the common case
 * from cache or a refresh token; it throws when the user has to be prompted
 * (expired session, new consent, MFA) — and, less obviously, when silent
 * renewal itself cannot run at all. Both mean the same thing to a customer, so
 * both redirect. Anything else is a genuine fault and is surfaced rather than
 * turned into a sign-in attempt.
 */
async function getToken(
  msal: IPublicClientApplication,
  account: AccountInfo
): Promise<string> {
  try {
    const result = await msal.acquireTokenSilent({ scopes: [apiScope], account });
    return result.accessToken;
  } catch (err) {
    if (needsInteractiveSignIn(err) && !redirectedVeryRecently()) {
      try {
        sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now()));
      } catch {
        // See above — proceed without the guard rather than not signing in.
      }
      await msal.acquireTokenRedirect({ ...loginRequest, account });
      // acquireTokenRedirect navigates away; this line is unreachable in
      // practice but keeps the function's return type honest.
      throw new ApiError(401, 'Redirecting to sign in…');
    }
    throw err;
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
}

export function createApiClient(
  msal: IPublicClientApplication,
  account: AccountInfo
): ApiClient {
  async function call<T>(path: string, init: RequestInit): Promise<T> {
    // Retries only the "database is waking" case — see wakeRetry.ts. Nothing
    // else is retried here: a 500 is a fault and repeating it just delays the
    // bad news.
    //
    // ⚠ AND ONLY FOR READS. This comment used to say a POST was not safe to
    // replay blindly while the loop below replayed it anyway. A waking database
    // now hands a write back to the person, who presses the button again —
    // a deliberate retry rather than an automatic one, which is the property a
    // non-idempotent request needs.
    for (let attempt = 1; ; attempt++) {
      const result = await attemptCall<T>(path, init);
      if (!result.waking) return result.value;
      if (!shouldRetryWake(init.method)) throw result.error;

      const delay = wakeRetryDelay(attempt);
      if (delay === null) throw result.error;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  async function attemptCall<T>(
    path: string,
    init: RequestInit
  ): Promise<{ waking: false; value: T } | { waking: true; error: ApiError }> {
    const token = await getToken(msal, account);

    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        ...init.headers,
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (response.status === 204) return { waking: false, value: undefined as T };

    const text = await response.text();
    const parsed: unknown = text ? safeJsonParse(text) : undefined;

    if (isWakingResponse(response.status, parsed)) {
      const body = parsed as { error?: string } | undefined;
      return {
        waking: true,
        error: new ApiError(503, body?.error ?? 'AcuTidy is starting up.'),
      };
    }

    if (!response.ok) {
      const body = parsed as { error?: string; detail?: unknown } | undefined;
      throw new ApiError(
        response.status,
        body?.error ?? `Request failed with status ${response.status}.`,
        body?.detail
      );
    }

    return { waking: false, value: parsed as T };
  }

  return {
    get: (path) => call(path, { method: 'GET' }),
    post: (path, body) =>
      call(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
    put: (path, body) =>
      call(path, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      }),
  };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { error: text.slice(0, 500) };
  }
}

// --- Response shapes, mirroring app/api ------------------------------------

export interface Tenant {
  tenantId: string;
  /** The paying party. Instances in one account share a subscription. */
  accountId: string;
  /** The production/sandbox pair — the $199 billable unit. */
  pairId: string;
  instanceKind: 'production' | 'sandbox';
  companyName: string;
  instanceBaseUrl: string;
  acumaticaCompany: string | null;
  clientId: string;
  endpointVersion: string;
  connectionStatus: 'pending' | 'connected' | 'error';
  connectionCheckedAt: string | null;
  connectionError: string | null;
  createdAt: string;
  userRole?: string;
  /** Did a record change recently when we last looked? null = never looked. */
  activitySeenRecently: boolean | null;
  /** Set when the customer disconnected this instance. Null means in use. */
  disconnectedAt: string | null;
  /** Set once the customer has answered the sandbox challenge. */
  instanceKindConfirmedAt: string | null;
  /**
   * When the Acumatica authorization is expected to lapse.
   *
   * Computed by the server, never here: the 30-day figure is an assumption
   * about the customer's Connected Application, and it needs exactly one
   * definition (app/api/src/acumatica/tokenPolicy.ts).
   */
  refreshToken?: RefreshTokenOutlook;
}

export interface RefreshTokenOutlook {
  issuedAt: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  state: 'unknown' | 'ok' | 'expiring' | 'expired' | 'longer-than-expected';
  lifetimeDays: number;
}

export interface EntityListResult {
  entity: string;
  records: Record<string, unknown>[];
  top: number;
  skip: number;
  hasMore: boolean;
  /**
   * Custom-attribute columns present on this page, as `attr.<AttributeID>`.
   * Discovered per page rather than declared — which attributes exist depends
   * on the customer's own instance and its customer classes.
   */
  attributeColumns?: string[];
}

export interface EntityDefinition {
  name: string;
  label: string;
  keyField: string;
  fields: string[];
}

/** Mirrors SubscriptionRecord in app/api/src/data/subscriptions.ts. */
export interface Subscription {
  accountId: string;
  /** Billable production/sandbox pairs — the Stripe quantity. */
  quantity: number;
  status: string;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface WritePolicy {
  /** Most changes this tenant may submit in one job right now. */
  maxChanges: number;
  /** True until an edit job has completed and not been undone. */
  firstJob: boolean;
  /** Manual stop for this tenant. */
  disabled: boolean;
  /** Why writing is blocked, when it is. Null when writing is allowed. */
  blockedReason: 'disabled' | 'subscription' | null;
}

export interface DuplicateCandidate {
  duplicateCandidateId: number;
  entityType: string;
  recordIdA: string;
  recordIdB: string;
  matchScore: number;
  status: 'pending' | 'merged' | 'dismissed';
  matchReason: string | null;
  summaryA: string | null;
  summaryB: string | null;
  detectedAt: string;
}

export interface HistoryCount {
  label: string;
  count: number;
  /** True when the real number is at least `count`, not exactly it. */
  atLeast: boolean;
}

export interface RecordHistory {
  recordId: string;
  counts: HistoryCount[];
  total: number;
  /** True if a count hit its cap, so `total` is a floor. */
  approximate: boolean;
}

/**
 * Everything the merge screen needs about one pair.
 *
 * The document counts are the reason this is a separate call from the
 * candidate list: they cost several metered Acumatica reads per record, so
 * they are fetched for the pair somebody actually opened, never for a list.
 */
export interface MergeDetail {
  candidate: DuplicateCandidate;
  keyField: string;
  fields: string[];
  recordA: Record<string, unknown> | null;
  recordB: Record<string, unknown> | null;
  historyA: RecordHistory;
  historyB: RecordHistory;
}

export interface JobChangeResult {
  recordId: string;
  fieldName: string;
  beforeValue: string | null;
  afterValue: string | null;
  writeStatus: 'pending' | 'written' | 'failed' | 'skipped';
  errorReason: string | null;
}

export interface Job {
  jobId: string;
  entityType: string;
  jobStatus: string;
  jobKind: string;
  createdAt: string;
  completedAt: string | null;
}

export interface JobResult {
  job: Job;
  changes: JobChangeResult[];
}
